import { useNavigate } from 'react-router-dom';
import { AlertCircle, AlertTriangle, BarChart2, Bell, KeyRound, ShieldAlert } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useNotifications, useMarkNotificationRead, useMarkAllNotificationsRead } from '../api/notifications';
import { useCursorPagination } from '../hooks/useCursorPagination';
import PageHeader from '../components/layout/PageHeader';
import Button from '../components/ui/Button';
import { CardListSkeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import ErrorState from '../components/ui/ErrorState';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { formatDateTime } from '../lib/dates';
import type { NotificationDto } from '../types/api';

const KIND_ICON: Record<string, LucideIcon> = {
  checkin_reminder:    Bell,
  escalation_t3:       AlertTriangle,
  escalation_t1:       AlertCircle,
  escalation_overdue:  AlertCircle,
  blocker_flagged:     AlertTriangle,
  weekly_pulse_prompt: BarChart2,
  weekly_report_ready: BarChart2,
  password_reset:      KeyRound,
  account_locked:      ShieldAlert,
};

const KIND_ICON_BG: Record<string, string> = {
  checkin_reminder:    'bg-primary/10',
  escalation_t3:       'bg-amber-50 dark:bg-amber-950/40',
  escalation_t1:       'bg-red-50 dark:bg-red-950/40',
  escalation_overdue:  'bg-red-50 dark:bg-red-950/40',
  blocker_flagged:     'bg-red-50 dark:bg-red-950/40',
  weekly_pulse_prompt: 'bg-violet-50 dark:bg-violet-950/40',
  weekly_report_ready: 'bg-emerald-50 dark:bg-emerald-950/40',
  password_reset:      'bg-muted',
  account_locked:      'bg-amber-50 dark:bg-amber-950/40',
};

const KIND_ICON_CLS: Record<string, string> = {
  checkin_reminder:    'text-primary',
  escalation_t3:       'text-amber-500',
  escalation_t1:       'text-red-500',
  escalation_overdue:  'text-red-500',
  blocker_flagged:     'text-red-500',
  weekly_pulse_prompt: 'text-violet-600 dark:text-violet-400',
  weekly_report_ready: 'text-emerald-600 dark:text-emerald-400',
  password_reset:      'text-muted-foreground',
  account_locked:      'text-amber-500',
};

const KIND_TITLE: Record<string, string> = {
  checkin_reminder:    'Check-in reminder',
  escalation_t3:       'Task approaching due date',
  escalation_t1:       'Task due soon',
  escalation_overdue:  'Task overdue',
  blocker_flagged:     'Blocker flagged',
  weekly_pulse_prompt: 'Weekly pulse survey ready',
  weekly_report_ready: 'Weekly leadership report ready',
  password_reset:      'Password reset requested',
  account_locked:      'Account locked',
};

function taskPayload(n: NotificationDto): { taskId?: string; taskTitle?: string } {
  if (!n.payload) return {};
  try {
    const parsed = JSON.parse(n.payload) as Record<string, unknown>;
    return { taskId: parsed['taskId'] as string | undefined, taskTitle: parsed['taskTitle'] as string | undefined };
  } catch { return {}; }
}

function NotificationItem({ n, onMarkRead }: { n: NotificationDto; onMarkRead: () => void }) {
  const navigate = useNavigate();
  const isT1 = n.kind === 'escalation_t1';
  const { taskId, taskTitle } = taskPayload(n);
  const title = KIND_TITLE[n.kind] ?? n.kind.replace(/_/g, ' ');
  const Icon   = KIND_ICON[n.kind]     ?? Bell;
  const iconBg = KIND_ICON_BG[n.kind]  ?? 'bg-muted';
  const iconCls = KIND_ICON_CLS[n.kind] ?? 'text-muted-foreground';

  return (
    <div className={cn('flex items-start gap-3 px-5 py-4', !n.isRead && 'bg-primary/5')}>
      <div className={cn('mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', iconBg)}>
        <Icon className={cn('h-4 w-4', iconCls)} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className={cn('text-sm', n.isRead ? 'text-muted-foreground' : 'font-medium text-foreground')}>{title}</p>
          {!n.isRead && <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />}
        </div>
        {taskTitle && <p className="mt-0.5 truncate text-xs text-muted-foreground">{taskTitle}</p>}
        <p className="mt-1 text-xs text-muted-foreground">{formatDateTime(n.sentAt)}</p>
        {isT1 && taskId && (
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={onMarkRead}
              className="rounded-md bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 dark:bg-emerald-950/40 dark:text-emerald-400"
            >
              On track
            </button>
            <button
              type="button"
              onClick={() => navigate(`/tasks/${taskId}`)}
              className="rounded-md bg-red-100 px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 dark:bg-red-950/40 dark:text-red-400"
            >
              I have a blocker
            </button>
          </div>
        )}
      </div>
      {!n.isRead && !isT1 && (
        <button
          type="button" onClick={onMarkRead}
          className="shrink-0 rounded text-xs text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Mark read
        </button>
      )}
    </div>
  );
}

export default function NotificationsPage() {
  const { cursor, hasPrev, goNext, goPrev } = useCursorPagination();
  const { data, isLoading, error, refetch } = useNotifications(cursor);
  const markRead    = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const hasUnread = data?.items.some((n) => !n.isRead) ?? false;

  if (isLoading) return <CardListSkeleton count={6} hasAction={false} />;
  if (error)     return <ErrorState onRetry={refetch} />;

  return (
    <div className="max-w-2xl">
      <PageHeader
        title="Notifications"
        actions={
          hasUnread ? (
            <Button size="sm" variant="ghost" loading={markAllRead.isPending} onClick={() => markAllRead.mutate()}>
              Mark all read
            </Button>
          ) : undefined
        }
      />

      {data?.items.length === 0 ? (
        <Card>
          <EmptyState icon={Bell} title="No notifications" description="You're all caught up." />
        </Card>
      ) : (
        <Card className="divide-y divide-border overflow-hidden p-0">
          {data?.items.map((n) => (
            <NotificationItem key={n.id} n={n} onMarkRead={() => markRead.mutate(n.id)} />
          ))}
        </Card>
      )}

      {(hasPrev || data?.hasMore) && (
        <div className="mt-4 flex justify-between">
          <Button variant="secondary" size="sm" disabled={!hasPrev} onClick={goPrev}>Previous</Button>
          <Button
            variant="secondary" size="sm"
            disabled={!data?.hasMore}
            onClick={() => data?.nextCursor && goNext(data.nextCursor)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
